import { Activity, ArrowDown, ArrowUp, Flame, HeartPulse, Moon } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { BASELINE_DAYS, SPARK_DAYS, sparkPath, type Metric, type Trend } from "../lib/wearable.ts";

const ICON: Record<Metric, LucideIcon> = {
  recoveryPct: HeartPulse,
  hrvMs: Activity,
  sleepMinutes: Moon,
  strain: Flame,
};
const TONE = { high: "text-gain", mid: "text-ink", low: "text-iron" } as const;

const SPARK_WIDTH = 100;
const SPARK_HEIGHT = 18;

/**
 * The wearable's read of today, each figure against this body's own last
 * month: an arrow for the gap to the average, a line for the last two weeks.
 * Drawn once, never animated: the screen stays on all session long.
 */
export function TodayWearable({ trends, className = "" }: { trends: Trend[]; className?: string }) {
  return (
    <section className={["card px-5 py-3.5", className].join(" ")} aria-label="Aujourd'hui">
      <dl className="num grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
        {trends.map((trend) => (
          <Figure key={trend.metric} trend={trend} />
        ))}
      </dl>
    </section>
  );
}

function Figure({ trend }: { trend: Trend }) {
  const Icon = ICON[trend.metric];
  const Arrow = trend.delta !== null && trend.delta < 0 ? ArrowDown : ArrowUp;
  const deltaTone = trend.better === null ? "text-ink-mute" : trend.better ? "text-gain" : "text-iron";
  const path = sparkPath(trend.spark, SPARK_WIDTH, SPARK_HEIGHT);
  const lastIndex = trend.spark.length - 1;

  return (
    <div className="min-w-0">
      <dt className="label flex items-center gap-1.5 text-[0.62rem]">
        <Icon size={13} strokeWidth={2.25} className="shrink-0" aria-hidden />
        {trend.label}
      </dt>
      <dd className="mt-1 flex items-baseline gap-1.5">
        {/* The value whole, always: the gap beside it gives way first. */}
        <span className={["shrink-0 text-[1.1rem] font-bold", trend.tone ? TONE[trend.tone] : "text-ink"].join(" ")}>
          {trend.value}
        </span>
        {trend.deltaLabel && (
          <span
            className={[
              "inline-flex min-w-0 items-center overflow-hidden text-[0.72rem] font-semibold whitespace-nowrap",
              deltaTone,
            ].join(" ")}
            title={`${trend.delta! > 0 ? "Au-dessus" : "En dessous"} de la moyenne des ${BASELINE_DAYS} derniers jours`}
          >
            <Arrow size={11} strokeWidth={3} aria-hidden />
            {trend.deltaLabel}
            <span className="sr-only">
              {trend.delta! > 0 ? " au-dessus" : " en dessous"} de la moyenne des {BASELINE_DAYS} derniers jours
            </span>
          </span>
        )}
      </dd>
      {path && (
        <div className="relative mt-1.5 h-[18px]">
          <svg
            viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full overflow-visible"
            role="img"
            aria-label={`${trend.label}, ${SPARK_DAYS} derniers jours`}
          >
            <path
              d={path}
              fill="none"
              stroke="var(--color-ink)"
              strokeOpacity={0.35}
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {/* Today, as a dot: drawn outside the stretched SVG, it stays round. */}
          {trend.spark[lastIndex] !== null && (
            <span
              aria-hidden
              className="absolute right-0 h-[5px] w-[5px] translate-x-1/2 -translate-y-1/2 rounded-full bg-ink"
              style={{ top: `${sparkY(trend.spark)}px` }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Where today's point sits on the line, on the same scale as `sparkPath`. */
function sparkY(values: (number | null)[]): number {
  const present = values.filter((value): value is number => value !== null);
  const low = Math.min(...present);
  const span = Math.max(...present) - low || 1;
  return 1 + (SPARK_HEIGHT - 2) * (1 - (values.at(-1)! - low) / span);
}
